import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth is carried by an httpOnly cookie (set by the backend on login). We send
// cookies on every request; the JWT itself is NEVER stored in localStorage.
axios.defaults.withCredentials = true;

// Non-sensitive marker so components can cheaply tell "is there a session?".
// The backend reads the httpOnly cookie first, so this value is never used as a
// real credential (any Authorization header built from it is ignored).
const SESSION_MARKER = "session";

const AuthContext = createContext(null);

function getStoredToken() {
  return localStorage.getItem("blumove_token");
}

function getStoredUser() {
  try {
    const u = localStorage.getItem("blumove_user");
    return u ? JSON.parse(u) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined=checking, null=not auth, object=auth
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data);
      localStorage.setItem("blumove_user", JSON.stringify(res.data));
    } catch {
      localStorage.removeItem("blumove_token");
      localStorage.removeItem("blumove_user");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password, expectedRole = null) => {
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password, portal: expectedRole });
      const data = res.data;
      // Belt-and-braces: enforce client-side too in case backend returns 200
      if (expectedRole && data.role && data.role !== expectedRole) {
        const err = new Error("ROLE_MISMATCH");
        err.code = "ROLE_MISMATCH";
        err.actualRole = data.role;
        throw err;
      }
      localStorage.setItem("blumove_token", SESSION_MARKER);
      localStorage.setItem("blumove_user", JSON.stringify(data));
      setUser(data);
      return data;
    } catch (e) {
      // Backend returned 403 with detail "role_mismatch:<actualRole>"
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403 && typeof detail === "string" && detail.startsWith("role_mismatch:")) {
        const actual = detail.split(":")[1];
        const err = new Error("ROLE_MISMATCH");
        err.code = "ROLE_MISMATCH";
        err.actualRole = actual;
        throw err;
      }
      throw e;
    }
  };

  const register = async (name, email, password, role) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password, role });
    const data = res.data;
    localStorage.setItem("blumove_token", SESSION_MARKER);
    localStorage.setItem("blumove_user", JSON.stringify(data));
    setUser(data);
    return data;
  };

  const logout = async () => {
    const token = getStoredToken();
    try {
      if (token) {
        await axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } });
      }
    } catch (e) { console.warn("logout request failed", e); }
    localStorage.removeItem("blumove_token");
    localStorage.removeItem("blumove_user");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
